import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'

const AuthSuccess = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { handleOAuthLogin } = useAuth()
  const { isNight } = useTheme()
  const [error, setError] = useState('')

  useEffect(() => {
    const processToken = async () => {
      const token = searchParams.get('token')

      if (token) {
        // Use AuthContext handler to set up token refresh
        const result = await handleOAuthLogin(token)

        if (result.success) {
          // Redirect based on user role
          setTimeout(() => {
            if (result.user?.role === 'admin') {
              navigate('/admin', { replace: true })
            } else {
              navigate('/portal', { replace: true })
            }
          }, 1000)
        } else {
          setError(result.message || 'Authentication failed')
          setTimeout(() => {
            navigate('/login', { replace: true })
          }, 2000)
        }
      } else {
        // No token, redirect to login
        navigate('/login', { replace: true })
      }
    }

    processToken()
  }, [searchParams, navigate, handleOAuthLogin])

  return (
    <div className={`min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 ${isNight ? 'bg-void' : 'bg-gray-50'}`}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`rounded-lg p-6 text-center ${isNight ? 'bg-void-light border border-void-elevated' : 'bg-white shadow-md'}`}>
          {error ? (
            <>
              <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${isNight ? 'bg-red-900/30' : 'bg-red-100'}`}>
                <svg className={`h-10 w-10 ${isNight ? 'text-red-400' : 'text-red-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>

              <h2 className={`text-2xl font-bold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                {isNight ? 'AUTHENTICATION FAILED' : 'Authentication Failed'}
              </h2>

              <p className={`mb-6 ${isNight ? 'text-red-400' : 'text-red-600'}`}>
                {error}
              </p>

              <p className={`text-sm ${isNight ? 'text-gray-400' : 'text-gray-500'}`}>
                Redirecting to login...
              </p>
            </>
          ) : (
            <>
              <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6 ${isNight ? 'bg-green-900/30' : 'bg-green-100'}`}>
                <svg className={`h-10 w-10 ${isNight ? 'text-green-400' : 'text-green-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h2 className={`text-2xl font-bold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
                {isNight ? 'ACCESS GRANTED' : 'Authentication Successful'}
              </h2>

              <p className={`mb-6 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
                {isNight ? 'Initializing portal access...' : 'Redirecting you to your portal...'}
              </p>

              <div className="flex justify-center">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isNight ? 'border-cyber-cyan' : 'border-primary-600'}`}></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthSuccess
